import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Watchers } from '../../../models/watchers.model';
import { Subject } from 'rxjs';
import { ObservationsService } from '../../../services/Observations/observations.service';
import { Project360Service } from '../../../modules/p-dashboard/project360.service';
import { TranslocoService } from '@ngneat/transloco';
import { ReplyAttached } from '../../../models/reply.model';
import { ObservationLog } from '../../../models/project.model';
import { WeeklyReportComment } from '../../../models/weeklyReports.model';
import { FuseAnimations } from '../../../../@fuse/animations';
import { FilePreviewerDialogComponent } from '../../../modules/p-dashboard/file-previewer-dialog/file-previewer-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { CustomFieldWithValue } from '../../../models/custom-fields.model';

@Component({
  selector: 'app-comments',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
  animations: FuseAnimations,
})
export class CommentsComponent implements OnInit, OnChanges {
  @Input() heading: string;
  @Input() replyPermission: boolean;
  @Input() logs: ObservationLog[];
  @Input() loading: boolean;
  @Input() reportComments: WeeklyReportComment[];
  @Input() comments: any;
  @Input() observationReplies: any[];
  @Input() commentId: string = '';
  @Input() id: string;
  @Input() isUploadShown: boolean = false;
  @Input() mentionableUsersList: Watchers[] = [];
  @Input() type: 'general' | 'note' | 'issue' | 'snag';
  @Input() hideComments: boolean = false;
  @Input() customFields: CustomFieldWithValue[] = [];
  @Input() isLoadingCustomFields: boolean = false;
  @Output() addReply = new EventEmitter();
  @Output() download = new EventEmitter();
  @Output() initializeMentionableUsers = new EventEmitter();
  @Output() replyAttachments = new EventEmitter();
  @Output() userMentionedEmitter = new EventEmitter();
  attachments: [] = [];
  userMentioned: Watchers[] = [];
  userSelected: Watchers;

  replyForm!: UntypedFormGroup;
  fileOptions = {
    maxFilesLimit: 10,
    maxFileSize: 200,
    allowedExtensions: [],
  };
  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
  private searchInput$: Subject<string> = new Subject<string>();
  duplicateSearchValue = '';
  mentionedUserTriggered: boolean = false;

  constructor(
    public dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private observationsService: ObservationsService,
    private _project: Project360Service,
    public _transloco: TranslocoService,
  ) {}

  @ViewChild('commentItem', { static: false }) commentItemElement: ElementRef;

  ngOnChanges(changes: SimpleChanges) {
    this.fileOptions.allowedExtensions = this._project.allowedExtensions
      .filter((value: string) => value !== 'kmz')
      .map((ext: string) => (ext != 'kmz' ? ext : ''));
    if (changes.commentId) {
      setTimeout(() => {
        this.scrollToComment();
      }, 1500);
    }
    // Re-initialize form when id changes to ensure observation_id is set correctly
    if (changes.id && this.id) {
      this.initForm();
    }
  }

  ngOnInit(): void {
    // Only initialize form if id is already available
    if (this.id) {
      this.initForm();
    }
    this.initializeMentionableUsers.emit(this.searchInput$);
  }

  scrollToComment() {
    const commentElement = document.getElementById(`comment-${this.commentId}`);
    if (commentElement) {
      // Scroll to the comment smoothly
      commentElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // Add the 'comment-highlight' class to start the fade-in highlight effect
      commentElement.classList.add('comment-highlight');

      // After the fade-in completes (1 second), start the fade-out
      setTimeout(() => {
        commentElement.classList.add('comment-fade-out'); // Add fade-out class for smooth fading out
      }, 1500); // Adjust timing to match the fade-in duration

      // Optionally, remove the classes after the full fade-out duration (3.5 seconds)
      setTimeout(() => {
        commentElement.classList.remove('comment-highlight', 'comment-fade-out');
      }, 3500); // Total time for both fade-in and fade-out
    }
  }

  initForm(): void {
    this.replyForm = this.fb.group({
      observation_id: [this.id, Validators.required],
      reply: [null, Validators.required],
      attached: [null],
      chips: this.fb.array([]),
    });
  }

  onSearchInputChange(searchValue: string): void {
    this.duplicateSearchValue = searchValue;
    const mentionTrigger = '@';
    const minSearchLength = 2;

    if (searchValue.length === 0) {
      this.initForm();
      this.userMentioned = [];
      this.userMentionedEmitter.emit(this.userMentioned);
      this.autocomplete.closePanel(); // Close autocomplete panel when input is empty
      this.mentionedUserTriggered = false;
      return;
    }

    if (searchValue.includes(mentionTrigger)) {
      this.mentionedUserTriggered = true;
      const query = searchValue.substring(searchValue.lastIndexOf(mentionTrigger) + 1);
      if (query.length >= minSearchLength) {
        this.autocomplete.openPanel();
        this.searchInput$.next(query);
      } else {
        this.autocomplete.closePanel();
      }
    } else {
      this.mentionedUserTriggered = false;
      this.autocomplete.closePanel();
    }
  }

  onTextareaFocus(): void {
    const searchValue = this.replyForm.get('reply').value;
    if (searchValue && searchValue.includes('@')) {
      this.autocomplete.openPanel();
    } else {
      this.autocomplete.closePanel();
    }
  }

  getUserInitials(user: Watchers): string {
    const initials = user.name.substring(0, 2).toUpperCase();
    return initials;
  }

  removeChip(index: number) {
    const chips = this.replyForm.get('chips') as FormArray;
    const chip = chips.at(index);
    const email = chip.value.slice(1);
    const userIndex = this.userMentioned.findIndex((u) => u.email === email);
    if (userIndex !== -1) {
      this.userMentioned.splice(userIndex, 1);
    }
    this.userMentionedEmitter.emit([this.userMentioned]);
    chips.removeAt(index);
  }

  onUserSelected(email: string) {
    if (email.startsWith('@')) {
      email = email.substring(1);
      email.trim();
    }

    const user = this.mentionableUsersList.find((u) => u.email === email);
    const name = user.name;
    const mention = `@${email}`;

    // get the current value of the input field
    let searchValue = this.replyForm.get('reply').value;

    // check if there is already a mention in the comment
    const existingMention = this.userMentioned.find((u) => u.email === email);
    if (existingMention) {
      // if the user is already mentioned, replace the old mention with the new one
      searchValue = searchValue.replace(`@${existingMention.name}`, mention);
    } else {
      // if the user is not already mentioned, add the new mention to the end of the comment
      searchValue = ` ${name}`;
      // add the selected user to the list of mentioned users
      this.userMentioned.push(user);

      // create a new chip with the user's email and a remove icon
      const chip = new FormControl(mention);
      const chips = this.replyForm.get('chips') as FormArray;
      chips.push(chip);
    }

    // remove residue of typed email
    this.duplicateSearchValue = this.cleanEmailResidue(this.duplicateSearchValue);

    // update the value of the input field

    this.replyForm.get('reply').setValue(this.duplicateSearchValue + searchValue);

    this.mentionedUserTriggered = false;
  }

  cleanEmailResidue(str: string): string {
    // Match any sequence starting with '@' followed by non-whitespace characters
    return str.replace(/@\S+/g, '');
  }

  removeAllChips() {
    const chips = this.replyForm.get('chips') as FormArray;

    // Clear the userMentioned array
    this.userMentioned = [];

    // Emit the updated userMentioned array
    this.userMentionedEmitter.emit([this.userMentioned]);

    // Clear the FormArray
    while (chips.length) {
      chips.removeAt(0);
    }
  }

  sendReply() {
    this.userMentionedEmitter.emit(this.userMentioned);
    this.replyAttachments.emit(this.attachments);
    this.addReply.emit(this.replyForm);
    this.replyForm.get('reply').setValue('');
    this.removeAllChips();
    this.attachments = [];
    this.isUploadShown = false;
    setTimeout(() => {
      let element = document.getElementById('replyField');
      element.classList.remove('mat-form-field-invalid');
      this.replyForm.markAsUntouched();
    }, 1000);
  }

  downloadFile(attache: ReplyAttached) {
    this.download.emit(attache);
  }

  openPreviewer(fileSrc: string, extension?: string, fileName?: string) {
    this.dialog.open(FilePreviewerDialogComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw', // Ensure the dialog doesn't shrink
      maxHeight: '100vh', // Ensure the dialog doesn't shrink
      panelClass: 'full-screen-dialog', // Custom class for additional styling
      data: { fileSrc: fileSrc, extension: extension, fileName: fileName },
    });
  }
}
